import { useNavigate, useLocation } from "react-router-dom";
import { Home, Users, Calendar, Menu as MenuIcon } from "lucide-react";
import { useMenu } from "@/context/MenuContext";

// Tiny uppercase section label.
export const SectionLabel = ({ children, icon, className = "" }) => (
  <div className={`flex items-center gap-1.5 text-[11px] font-semibold uppercase tracking-[0.1em] text-[#666666] ${className}`}>
    {icon}
    {children}
  </div>
);

// Bordered card (light) or hero card (dark).
export const Card = ({ children, dark = false, className = "", ...props }) => (
  <div
    className={`rounded-[16px] p-5 ${
      dark ? "bg-[#111111] text-white" : "border border-[#e9e6e1] bg-white"
    } ${className}`}
    {...props}
  >
    {children}
  </div>
);

// Selectable chip (multi or single use).
export const Chip = ({ children, selected, onClick, testid, full = false }) => (
  <button
    type="button"
    data-testid={testid}
    onClick={onClick}
    className={`inline-flex h-9 items-center justify-center rounded-full px-4 text-[13px] font-medium transition-all duration-150 active:scale-[0.97] ${
      full ? "w-full" : ""
    } ${
      selected
        ? "bg-[#111111] text-white"
        : "bg-[#f2f0ec] text-[#666666] hover:bg-[#eceae5]"
    }`}
  >
    {children}
  </button>
);

// Static info pill.
export const Pill = ({ children, tone = "neutral", className = "", ...rest }) => {
  const tones = {
    neutral: "bg-[#f2f0ec] text-[#666666]",
    dark: "bg-[#111111] text-white",
    light: "bg-white text-[#111111]",
    peach: "bg-[var(--jk-peach)] text-[#111111]",
    periwinkle: "bg-[var(--jk-periwinkle)] text-[#111111]",
  };
  return (
    <span {...rest} className={`inline-flex items-center rounded-full px-3 py-1 text-[12px] font-semibold ${tones[tone]} ${className}`}>
      {children}
    </span>
  );
};

// Single/multi select pill group.
export const PillGroup = ({ options, value, onChange, multi = false, testidPrefix = "pill" }) => {
  const isSel = (o) => (multi ? (value || []).includes(o) : value === o);
  const toggle = (o) => {
    if (multi) {
      const set = new Set(value || []);
      set.has(o) ? set.delete(o) : set.add(o);
      onChange([...set]);
    } else {
      onChange(o);
    }
  };
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <Chip key={o} selected={isSel(o)} onClick={() => toggle(o)} testid={`${testidPrefix}-${o.replace(/\s+/g, "-").toLowerCase()}`}>
          {o}
        </Chip>
      ))}
    </div>
  );
};

// Thin progress bar (onboarding).
export const ProgressBar = ({ value }) => (
  <div className="h-1 w-full overflow-hidden rounded-full bg-[#e5e5e5]">
    <div className="h-full rounded-full bg-[#111111] transition-all duration-300" style={{ width: `${value}%` }} />
  </div>
);

// Toggle switch.
export const Switch = ({ on, onChange, testid }) => (
  <button
    type="button"
    role="switch"
    aria-checked={on}
    data-testid={testid}
    onClick={() => onChange(!on)}
    className={`relative h-[28px] w-[48px] shrink-0 rounded-full transition-colors duration-200 ${on ? "bg-[#111111]" : "bg-[#e5e5e5]"}`}
  >
    <span className={`absolute top-[3px] h-[22px] w-[22px] rounded-full bg-white shadow-sm transition-all duration-200 ${on ? "left-[23px]" : "left-[3px]"}`} />
  </button>
);

// Initials avatar.
export const Avatar = ({ name = "", size = 44, photo = "", className = "" }) => {
  const initials = (name || "")
    .split(" ")
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0])
    .join("")
    .toUpperCase() || "?";
  if (photo) {
    return (
      <img
        src={photo}
        alt={name}
        className={`shrink-0 rounded-full border border-[#e5e5e5] object-cover ${className}`}
        style={{ width: size, height: size }}
      />
    );
  }
  return (
    <div
      className={`flex shrink-0 items-center justify-center rounded-full bg-[#f2f0ec] font-semibold text-[#111111] ${className}`}
      style={{ width: size, height: size, fontSize: size * 0.34 }}
    >
      {initials}
    </div>
  );
};

// Bottom navigation (Home / Clients / Calendar / Menu drawer).
export const BottomNav = () => {
  const navigate = useNavigate();
  const { pathname } = useLocation();
  const { isOpen, open } = useMenu();
  const items = [
    { key: "home", label: "Home", icon: Home, path: "/dashboard" },
    { key: "clients", label: "Clients", icon: Users, path: "/clients" },
    { key: "calendar", label: "Calendar", icon: Calendar, path: "/calendar" },
  ];
  const active = (p) => pathname === p || (p !== "/dashboard" && pathname.startsWith(p));
  return (
    <nav className="flex h-16 shrink-0 items-stretch border-t border-[#e9e6e1] bg-white">
      {items.map(({ key, label, icon: Icon, path }) => {
        const on = active(path);
        return (
          <button
            key={key}
            data-testid={`nav-${key}`}
            onClick={() => navigate(path)}
            className="flex flex-1 flex-col items-center justify-center gap-1"
          >
            <Icon size={21} strokeWidth={1.75} className={on ? "text-[#111111]" : "text-[#aaaaaa]"} />
            <span className={`text-[10px] font-medium ${on ? "text-[#111111]" : "text-[#aaaaaa]"}`}>{label}</span>
          </button>
        );
      })}
      <button
        key="menu"
        data-testid="nav-menu"
        onClick={open}
        className="flex flex-1 flex-col items-center justify-center gap-1"
      >
        <MenuIcon size={21} strokeWidth={1.75} className={isOpen ? "text-[#111111]" : "text-[#aaaaaa]"} />
        <span className={`text-[10px] font-medium ${isOpen ? "text-[#111111]" : "text-[#aaaaaa]"}`}>Menu</span>
      </button>
    </nav>
  );
};

// Spinner.
export const Spinner = ({ className = "" }) => (
  <div className={`h-6 w-6 animate-spin rounded-full border-2 border-[#e5e5e5] border-t-[#111111] ${className}`} />
);

export const FullScreenLoader = () => (
  <div className="flex h-[100dvh] items-center justify-center bg-[var(--jk-paper)]">
    <Spinner />
  </div>
);
